import { css } from "@emotion/react";
import { getIsoCodeWithIntFallback, jsHooks } from "@swo/shared";
import { SwarovskiOptikLocaleFrontend, type LocaleSuggestionOverlayTranslation } from "@swo/storefront";
import { useRef, useState } from "react";
import { useBodyScrollLock } from "../../hooks";
import { useContentFadeout } from "../../hooks/use-content-fadeout";
import { useStore } from "../../store/hooks/use-store";
import { CSS_MQ } from "../../styles/breakpoints";
import { fontColor, FONTS, fontSize } from "../../styles/fonts";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import { ModalRootPortalEntrance } from "../_shared-components/technical/ModalRootPortalEntrance";
import { PageHeadlineMedium } from "../_shared-components/ui-elements/PageHeadline";
import { SwarovskiButton } from "../_shared-components/ui-elements/SwarovskiButton";
import { LanguagePickerLayout, LanguagePickerPropsT } from "../HeaderComponent/layout/LanguagePickerLayout";
import { LanguagePickerCountryFlag } from "../LanguagePickerComponent/LanguagePickerCountryFlag";
import { CloseButtonSvg2 } from "../../assets";
import { T } from "../T";

type State = "inactive" | "suggestion" | "language-picker";

export const LocaleSuggestionOverlay: ReactTFC<{
  languagePicker: Omit<LanguagePickerPropsT, "locale">;
  locale: SwarovskiOptikLocaleFrontend;
  localeSuggestionsTranslations: LocaleSuggestionOverlayTranslation;
}> = props => {
  const store = useStore();
  const [state, setState] = useState<State>(store.state.withLocaleMismatchOverlay ? "suggestion" : "inactive");

  const activeState = state === "suggestion" || state === "language-picker";

  useBodyScrollLock(activeState);
  useContentFadeout(activeState);

  const ref = useRef<HTMLDivElement>(null);

  // const onDismiss = useCallback(() => {
  //   store.dispatch({ type: "set-user-preferred-locale", value: props.locale.code });
  // }, []);

  if (!store.state.withLocaleMismatchOverlay && state !== "inactive") {
    setState("inactive");
  }

  // useClickOutside(ref, onDismiss, true);

  function onChangeRequested() {
    setState("language-picker");
  }

  function onAcceptLocale() {
    store.dispatch({ type: "set-user-preferred-locale", value: props.locale.code });
  }

  return state === "suggestion" ? (
    <ModalRootPortalEntrance>
      <div css={modalWrapperStyles}>
        <div
          className={jsHooks.localeSuggestionOverlay}
          ref={ref}
          css={css`
            width: calc(100% - 40px);

            margin-bottom: 20px;

            ${CSS_MQ.atLeastSm} {
              width: 500px;
              margin-bottom: 0;
            }

            background: var(--so-green-white);
          `}
        >
          <div
            css={css`
              --s: 20px;
              ${CSS_MQ.atLeastSm} {
                --s: 30px;
              }

              margin: var(--s) var(--s) 0 var(--s);
              padding-bottom: var(--s);
              border-bottom: 1px solid #bfbfbf;
            `}
          >
            <PageHeadlineMedium>
              <T>{props.localeSuggestionsTranslations["locale_suggestion_overlay.headline"]}</T>
            </PageHeadlineMedium>

            <div
              css={css`
                margin-top: var(--s);

                display: flex;
                align-items: center;
                justify-content: space-around;
              `}
            >
              <div className={"u-d-flex u-ai-center"}>
                <LanguagePickerCountryFlag
                  countryIsoCode={getIsoCodeWithIntFallback(props.locale.country)}
                  css={css`
                    margin-right: 10px;
                    color: ${CSS_COLORS.White};
                  `}
                />
                <span
                  className={"u-uppercase"}
                  css={[
                    FONTS.SwarovskiFutura500,
                    fontColor.black,
                    fontSize.x2,
                    css`
                      margin-top: 2px; //visual centering
                    `,
                  ]}
                >
                  {props.locale.country === "INTERNATIONAL"
                    ? props.localeSuggestionsTranslations.international.value
                    : props.locale.country.label}
                </span>
              </div>
              <div
                css={css`
                  color: ${CSS_COLORS.Green};

                  > * {
                    text-transform: initial !important;
                  }
                `}
              >
                <SwarovskiButton variant={"outline-inherit"} as={"button"} size={"tiny"} onClick={onChangeRequested}>
                  {props.locale.language.label}
                </SwarovskiButton>
              </div>
            </div>
          </div>

          <div
            css={css`
              padding: 30px 50px;
              display: flex;
              justify-content: center;
              color: var(--so-green);
            `}
          >
            <SwarovskiButton variant={"plain-underline"} as={"button"} onClick={onChangeRequested}>
              {props.localeSuggestionsTranslations.changeCurrent.value}
            </SwarovskiButton>
          </div>

          <div
            css={css`
              padding: 20px 50px;
              background: var(--so-green-dark);
              display: flex;
              justify-content: center;
              color: ${CSS_COLORS.White};
            `}
          >
            <SwarovskiButton
              variant={"normal"}
              as={"button"}
              onClick={onAcceptLocale}
              className={jsHooks.localeSuggestionOverlayAccept}
            >
              {props.localeSuggestionsTranslations.acceptCurrent.value}
            </SwarovskiButton>
          </div>
        </div>
      </div>
    </ModalRootPortalEntrance>
  ) : state === "language-picker" ? (
    <ModalRootPortalEntrance>
      <div className={"u-flex-centered bg-cool-mint"} css={modalWrapperStyles}>
        <div
          css={css`
            position: relative;

            padding: 20px;
            max-height: 100%;

            overflow-y: auto;

            &::-webkit-scrollbar {
              width: 3px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            scrollbar-width: none; // firefox

            &::-webkit-scrollbar-thumb {
              background: ${CSS_COLORS.Green};
            }
          `}
        >
          <button
            onClick={() => {
              store.dispatch({ type: "set-user-preferred-locale", value: props.locale.code });
            }}
            css={css`
              position: absolute;
              right: 16px;
              top: 16px;

              width: 40px;
              height: 40px;

              svg {
                width: 32px;
                height: 32px;
              }

              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <CloseButtonSvg2 strokeWidth={4} />
          </button>

          <LanguagePickerLayout locale={props.locale} {...props.languagePicker} compact={true} />
        </div>
      </div>
    </ModalRootPortalEntrance>
  ) : null;
};

const modalWrapperStyles = css`
  position: fixed;
  z-index: 1000;

  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  display: flex;
  justify-content: center;

  overflow-y: scroll;

  align-items: flex-end;
  ${CSS_MQ.atLeastSm} {
    align-items: center;
  }

  &.bg-cool-mint {
    background: ${CSS_COLORS.CoolMint};
  }
`;
